import dayjs from "dayjs";
import type { ContainerWithInfo } from "../pages/ContainersAdmin";
import { downloadCsv } from "./adminTableCsv";

export type ContainerListStats = {
  totalCBM: number;
  storageCBM: number;
  totalWeight: number;
  bookingsCount: number;
  uniqueProducts: number;
};

/** Share of container CBM that has no delivery address (goes into storage). */
export function formatStorageShare(
  stats: Pick<ContainerListStats, "totalCBM" | "storageCBM"> | undefined,
): string {
  if (!stats || stats.totalCBM <= 0) return "-";
  const percent = Math.round((stats.storageCBM / stats.totalCBM) * 100);
  const storage = stats.storageCBM.toFixed(1);
  const total = stats.totalCBM.toFixed(1);
  return `${storage} / ${total} m³ (${percent}%)`;
}

export function formatDaysToEta(eta: string | null | undefined): string {
  if (!eta) return "-";
  const diff = dayjs(eta).startOf("day").diff(dayjs().startOf("day"), "day");
  if (diff < 0) return `${Math.abs(diff)} days ago`;
  if (diff === 0) return "Today";
  return `${diff} days`;
}

function statusLabel(status: string | null | undefined): string {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function portLabel(
  port: { name: string; country?: string | null } | undefined,
): string {
  if (!port?.name) return "-";
  return port.country ? `${port.name} (${port.country})` : port.name;
}

function dateLabel(value: string | null | undefined): string {
  return value ? dayjs(value).format("DD/MM/YYYY") : "-";
}

const HEADERS = [
  "Reference",
  "Transport Type",
  "Status",
  "Origin",
  "Destination",
  "ETD",
  "Delayed ETD",
  "ETA",
  "Customer ETA",
  "Days to ETA",
  "Bookings",
  "Products",
  "Weight (kg)",
  "CBM (m³)",
  "Storage %",
  "Companies",
  "Bill of lading (B/L)",
  "Tracking",
];

function containerToRow(
  container: ContainerWithInfo,
  stats: ContainerListStats | undefined,
  billOfLading: string,
): string[] {
  const companyCount = new Set(
    (container.booking_list || [])
      .map((booking) => booking.company_id)
      .filter((id): id is number => id != null),
  ).size;

  return [
    container.reference ||
      `EFF${container.id}${container.transport_type_info?.abbreviation || ""}`,
    container.transport_type_info?.name || "-",
    statusLabel(container.status),
    portLabel(container.origin_port_info),
    portLabel(container.destination_port_info),
    dateLabel(container.etd),
    dateLabel(container.delayed_etd),
    dateLabel(container.eta),
    dateLabel(container.eta_customer),
    formatDaysToEta(container.eta),
    stats ? String(stats.bookingsCount) : "-",
    stats ? String(stats.uniqueProducts) : "-",
    stats ? stats.totalWeight.toFixed(2) : "-",
    stats ? stats.totalCBM.toFixed(3) : "-",
    formatStorageShare(stats),
    String(companyCount),
    billOfLading || "-",
    container.track_and_trace || "-",
  ];
}

export function downloadAdminContainersCsv(
  containers: ContainerWithInfo[],
  statsById: Record<number, ContainerListStats>,
  billOfLadingById: Record<number, string>,
  filename = `containers-${dayjs().format("YYYY-MM-DD")}.csv`,
): void {
  downloadCsv(
    HEADERS,
    containers.map((container) =>
      containerToRow(
        container,
        statsById[container.id],
        billOfLadingById[container.id] || "",
      ),
    ),
    filename,
  );
}
